import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CircleAlert, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router";
import { api } from "../../api/client";
import { StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert } from "../../components/ui";
import type { ShellContext } from "../AppShell";

// "Feedback" (prototype page `feedback`): what guests left through "Lasă un feedback" on the public menu —
// KPIs, distribution of ratings, filters, newest/oldest, mark reviewed. Guests stay anonymous.

interface Fb {
  id: string;
  menu: string;
  rating: number;
  message: string;
  lang: string;
  status: "new" | "reviewed";
  createdAt: number;
}

const stars = (n: number) => "★".repeat(n) + "☆".repeat(5 - n);
const when = (ms: number) =>
  new Intl.DateTimeFormat("ro-RO", { dateStyle: "medium", timeStyle: "short" }).format(new Date(ms));

export function FeedbackPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const canEdit = membership.role !== "staff";
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["feedback", rid],
    queryFn: () => api<{ feedback: Fb[] }>("GET", `/restaurants/${rid}/feedback`),
  });
  const [filter, setFilter] = useState<"all" | "new" | "reviewed">("all");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const all = q.data?.feedback ?? [];
  const n = all.length;
  const nNew = all.filter((x) => x.status === "new").length;
  const avg = n ? all.reduce((a, x) => a + x.rating, 0) / n : 0;
  const list = all
    .filter((x) => filter === "all" || x.status === filter)
    .filter((x) => rating === "all" || String(x.rating) === rating)
    .sort((a, b) => (sort === "desc" ? b.createdAt - a.createdAt : a.createdAt - b.createdAt));

  async function mark(id: string, status: "reviewed" | "new") {
    try {
      await api("POST", `/restaurants/${rid}/feedback/${id}/${status}`);
      if (status === "reviewed")
        toast("Marcat ca revizuit.", async () => {
          await api("POST", `/restaurants/${rid}/feedback/${id}/new`);
          await qc.invalidateQueries({ queryKey: ["feedback", rid] });
        });
      else toast("Marcat din nou ca nou.");
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      await qc.invalidateQueries({ queryKey: ["feedback", rid] });
    }
  }

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Feedback</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 640 }}>
          Ce au lăsat clienții prin „Lasă un feedback” din meniul public. Clienții rămân anonimi.
        </p>
      </div>
      {q.isPending && (
        <p className="muted" role="status" style={{ margin: 0 }}>
          Se încarcă feedback-ul…
        </p>
      )}
      {q.isError && <Alert tone="error">{q.error.message}</Alert>}
      {q.isSuccess && n === 0 ? (
        <section className="card empty">
          <MessageSquare size={28} aria-hidden="true" />
          <strong>Niciun feedback încă</strong>
          <p style={{ margin: 0 }}>
            Clienții pot lăsa o notă și un mesaj din meniul public, cu butonul „Lasă un feedback” de la final.
          </p>
        </section>
      ) : q.isSuccess ? (
        <>
          <section className="card" aria-label="Indicatori">
            <div className="stat-grid">
              <div className="stat">
                <span className="v">{n}</span>
                <span className="k">Feedback total</span>
              </div>
              <div className="stat">
                <span className="v">{avg.toFixed(1).replace(".", ",")}</span>
                <span className="k">Nota medie din 5</span>
              </div>
              <div className="stat">
                <span className="v">{nNew}</span>
                <span className="k">Noi, necitite</span>
              </div>
              <div className="stat">
                <span className="v">{n - nNew}</span>
                <span className="k">Revizuite</span>
              </div>
            </div>
            <ul className="stack" style={{ gap: 4, listStyle: "none", padding: 0, margin: "14px 0 0" }}>
              {[5, 4, 3, 2, 1].map((x) => {
                const c = all.filter((f) => f.rating === x).length;
                return (
                  <li key={x} className="row" style={{ gap: 8, fontSize: 14 }}>
                    <span style={{ width: 34 }}>{x} ★</span>
                    <span style={{ flex: 1, height: 8, background: "var(--color-bg)", borderRadius: 4 }}>
                      <span
                        style={{
                          display: "block",
                          height: 8,
                          width: `${n ? Math.round((c / n) * 100) : 0}%`,
                          background: "#CFC4F2",
                          borderRadius: 4,
                        }}
                      />
                    </span>
                    <span style={{ width: 28, textAlign: "right" }}>{c}</span>
                  </li>
                );
              })}
            </ul>
          </section>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {(
              [
                ["all", "Toate", n],
                ["new", "Noi", nNew],
                ["reviewed", "Revizuite", n - nNew],
              ] as const
            ).map(([k, label, c]) => (
              <button
                key={k}
                type="button"
                aria-pressed={filter === k}
                className={`btn btn-sm ${filter === k ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setFilter(k)}
              >
                {label} ({c})
              </button>
            ))}
            <label className="sr-only" htmlFor="fb-rating">
              Filtrează după notă
            </label>
            <select
              id="fb-rating"
              className="field-input"
              style={{ height: 40, width: "auto" }}
              value={rating}
              onChange={(e) => setRating(e.target.value)}
            >
              <option value="all">Toate notele</option>
              {[5, 4, 3, 2, 1].map((x) => (
                <option key={x} value={String(x)}>
                  {x} {x === 1 ? "stea" : "stele"}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="fb-sort">
              Ordonează după dată
            </label>
            <select
              id="fb-sort"
              className="field-input"
              style={{ height: 40, width: "auto" }}
              value={sort}
              onChange={(e) => setSort(e.target.value as "desc" | "asc")}
            >
              <option value="desc">Cele mai noi</option>
              <option value="asc">Cele mai vechi</option>
            </select>
          </div>
          {list.length ? (
            <ul className="verify-list">
              {list.map((x) => (
                <li key={x.id} className="verify-row">
                  <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                    <span style={{ letterSpacing: 2, color: "#8A5300" }}>
                      <span aria-hidden="true">{stars(x.rating)}</span>
                      <span className="sr-only">{`Nota ${x.rating} din 5`}</span>
                    </span>
                    <span style={{ fontWeight: x.status === "new" ? 600 : 400, overflowWrap: "anywhere" }}>
                      {x.message || <span className="muted">Doar notă, fără mesaj.</span>}
                    </span>
                    <span className="caption muted">
                      {when(x.createdAt)} · {x.menu || "Meniu"} · Client anonim
                      {x.lang === "en" ? " · în engleză" : ""}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    {x.status === "new" ? (
                      <StatusChip tone="warning" icon={CircleAlert} label="Nou" />
                    ) : (
                      <StatusChip tone="success" icon={Check} label="Revizuit" />
                    )}
                    {canEdit &&
                      (x.status === "new" ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => mark(x.id, "reviewed")}
                        >
                          Marchează revizuit
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => mark(x.id, "new")}
                        >
                          Marchează ca nou
                        </button>
                      ))}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">
              Niciun feedback pentru filtrele alese.{" "}
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setFilter("all");
                  setRating("all");
                }}
              >
                Șterge filtrele
              </button>
            </p>
          )}
        </>
      ) : null}
    </>
  );
}
